import { useState } from "react";
import { baglantiSemasi, yerelGunBasi } from "@koclukpanel/contracts";
import {
  DURUM_SINIFLARI,
  DurumRozeti,
  HAFTA_GUNLERI,
  Ikon,
  Rozet,
  cn,
  type UnkDurum,
} from "@koclukpanel/ui";
import type { ProgramGorevi } from "./api";
import {
  OGRENCI_SUZGECLERI,
  dersKisaltmasi,
  gorunenDurum,
  noktaliTarih,
  suzgeceUyar,
  type OgrenciSuzgeci,
} from "./program-durum";

/**
 * ÖĞRENCİ GÖZÜNDEN HAFTA — UNİKOÇ `WeeklySchedule-ViewAsStudent` (sürüm 9a)
 *
 * Koç öğrencinin programı nasıl gördüğünü SALT OKUNUR izliyor (F-SCH-17).
 * Hiçbir uç çağrılmıyor ve hiçbir alan yazılamıyor: veri program ekranının
 * zaten okuduğu haftadan geliyor. Öğrenci uygulamasının bileşeni
 * (`HaftalikProgramGorunumu`) burada kullanılmıyor, çünkü o ekran öğrenci
 * uygulamasının kendi tasarımında kalıyor (sürüm 9b) — koçun gördüğü bu
 * görünüm ise müşterinin eski panelindeki kart düzeni.
 *
 * Gün sırası PAZARTESİ başlıyor (karar 07.10.2026, ONERI §5.2); eski panel
 * Cumartesi'den başlatıyordu, o tutarsızlık taklit edilmiyor.
 */
export function ProgramOgrenciGozu({
  ogrenciAdi,
  basHarfler,
  gunler,
  gorevler,
  onOncekiHafta,
  onBuHafta,
  onSonrakiHafta,
  onDuzenlemeyeDon,
}: {
  ogrenciAdi: string;
  basHarfler: string;
  /** Haftanın yedi günü, Pazartesi'den — `YYYY-AA-GG`. */
  gunler: readonly string[];
  gorevler: readonly ProgramGorevi[];
  onOncekiHafta: () => void;
  onBuHafta: () => void;
  onSonrakiHafta: () => void;
  onDuzenlemeyeDon: () => void;
}) {
  const bugun = yerelGunBasi().toISOString().slice(0, 10);
  const [suzgec, setSuzgec] = useState<OgrenciSuzgeci>("Tümü");
  /*
    Görevi olan günler AÇIK başlıyor, boş günler kapalı: eski panelde de
    dolu günler açık, sonrakiler yalnız başlık şeridiydi. Boş bir günü açık
    çizmek, sayfayı "görev yok" yazılarıyla uzatmak olurdu.
  */
  const [acik, setAcik] = useState<ReadonlySet<string>>(
    () => new Set(gunler.filter((g) => gorevler.some((x) => x.itemDate.slice(0, 10) === g))),
  );
  const gunDegistir = (gun: string): void =>
    setAcik((once) => {
      const yeni = new Set(once);
      if (yeni.has(gun)) yeni.delete(gun);
      else yeni.add(gun);
      return yeni;
    });

  const ilk = gunler[0] ?? "";
  const son = gunler[gunler.length - 1] ?? "";

  return (
    <section aria-label="Öğrenci haftalık önizlemesi" className="flex min-w-0 flex-col gap-4">
      {/* Başlık kartı — beyaz, eski paneldeki ayrı kalıp (tarama-2 §3.3/1). */}
      <div className="bg-notr-0 border-mor-kenar flex flex-col gap-3 rounded-[1.25rem] border p-4 shadow-(--golge-unk-kart) md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <span
            aria-hidden="true"
            className="text-notr-0 grid size-14 shrink-0 place-items-center rounded-full bg-(image:--gradyan-unk-birincil) text-lg font-bold"
          >
            {basHarfler}
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-2">
              <strong className="text-metin-koyu text-lg font-bold">{ogrenciAdi}</strong>
              <span className="text-notr-0 rounded-rozet bg-(image:--gradyan-unk-birincil) px-2 py-0.5 text-[0.6875rem] font-bold">
                Haftalık Program
              </span>
            </p>
            <p className="text-metin-ikincil mt-0.5 flex items-center gap-1.5 text-sm tabular-nums">
              <Ikon ad="takvim" className="size-4" />
              {noktaliTarih(ilk)} — {noktaliTarih(son)}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <KareDugme etiket="Önceki hafta" onClick={onOncekiHafta}>
              <span aria-hidden="true">‹</span>
            </KareDugme>
            <KareDugme etiket="Bu hafta" onClick={onBuHafta}>
              <Ikon ad="takvim" className="size-4" />
            </KareDugme>
            <KareDugme etiket="Sonraki hafta" onClick={onSonrakiHafta}>
              <span aria-hidden="true">›</span>
            </KareDugme>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ul aria-label="Renklerin anlamı" className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {(
              [
                ["tamamlandi", "Tamamlandı"],
                ["bekliyor", "Bekliyor (Bugün)"],
                ["planli", "Planlanmış"],
                ["eksik", "Tamamlanmadı"],
              ] as const
            ).map(([durum, etiket]) => (
              <li key={durum} className="text-metin-ikincil flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={cn("size-2.5 rounded-full", DURUM_SINIFLARI[durum].nokta)}
                />
                {etiket}
              </li>
            ))}
          </ul>
          {/*
            DÖNÜŞ DÜĞMESİ BU KARTTA: eski panelde öğrenci görünümü ayrı bir
            sayfaydı ve geri dönüş tarayıcının geri tuşuydu. Burada aynı
            ekranın iki hâli var; koçun düzenlemeye dönmesi için yazılı bir yol
            gerekiyor.
          */}
          <button
            type="button"
            onClick={onDuzenlemeyeDon}
            className="min-h-dokunma border-mor-kesikli text-mor-700 hover:bg-mor-50 rounded-xl border border-dashed px-4 text-sm font-semibold"
          >
            ← Düzenlemeye dön
          </button>
        </div>
      </div>

      {/* Süzgeç çubuğu — "Görünüm:" + haplar (aktif: mor ince çerçeve). */}
      <div className="bg-notr-0 border-mor-kenar flex flex-wrap items-center gap-3 rounded-[1.25rem] border px-4 py-3">
        <span className="text-metin-ikincil text-sm">Görünüm:</span>
        <div
          role="group"
          aria-label="Görev süzgeci"
          className="ml-auto flex flex-wrap justify-end gap-2"
        >
          {OGRENCI_SUZGECLERI.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={suzgec === s}
              onClick={() => setSuzgec(s)}
              className={cn(
                "min-h-dokunma rounded-rozet text-metin-koyu bg-notr-0 px-4 text-sm font-semibold",
                suzgec === s ? "ring-mor-500 ring-2" : "ring-mor-kenar hover:bg-mor-50 ring-1",
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {gunler.map((gun, i) => {
        const gunun = gorevler
          .filter((g) => g.itemDate.slice(0, 10) === gun && suzgeceUyar(g, suzgec, bugun))
          .sort((a, b) => a.sortOrder - b.sortOrder);
        // Süzgeç seçiliyken eşleşmesi olmayan gün gizli: "Tamamlanan"a basan koç
        // yedi boş şerit arasında arama yapmasın.
        if (suzgec !== "Tümü" && gunun.length === 0) return null;
        const gunAcik = acik.has(gun) || suzgec !== "Tümü";
        const govdeId = `ogrenci-gozu-${gun}`;
        return (
          <section
            key={gun}
            className="bg-notr-0 border-mor-kenar overflow-hidden rounded-[1.25rem] border"
          >
            <h3>
              <button
                type="button"
                aria-expanded={gunAcik}
                aria-controls={govdeId}
                onClick={() => gunDegistir(gun)}
                className={cn(
                  "min-h-dokunma text-metin-koyu flex w-full items-center gap-2 px-4 py-3 text-left",
                  gunAcik && "bg-mor-50 border-mor-kenar border-b",
                )}
              >
                <Ikon ad="takvim" className="size-4" />
                <span className="flex-1">{HAFTA_GUNLERI[i]?.ad}</span>
                <span className="text-metin-ikincil text-sm tabular-nums">{noktaliTarih(gun)}</span>
                <Ikon
                  ad="asagiOk"
                  className={cn("size-4 transition-transform", gunAcik && "rotate-180")}
                />
              </button>
            </h3>
            {gunAcik && (
              <div id={govdeId} className="p-4">
                {gunun.length === 0 ? (
                  <p className="text-metin-ikincil text-sm">Bu gün için görev yok.</p>
                ) : (
                  <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {gunun.map((g) => (
                      <OgrenciKarti key={g.id} gorev={g} bugun={bugun} />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        );
      })}
    </section>
  );
}

/*
  Kartın üstten beyaza eriyen zemini (tarama-2 §2.3 "durum kartları"): sınıf
  adları TAM yazılı, Tailwind birleştirilmiş adı taramıyor.
*/
const KART_ZEMINI: Record<UnkDurum, string> = {
  tamamlandi: "from-tamam-zemin",
  eksik: "from-eksik-zemin",
  bekliyor: "from-bekliyor-zemin",
  planli: "from-planli-zemin",
};

function OgrenciKarti({ gorev: g, bugun }: { gorev: ProgramGorevi; bugun: string }) {
  const [notAcik, setNotAcik] = useState(false);
  const { durum, etiket } = gorunenDurum(g, bugun);
  return (
    <li
      className={cn(
        "to-notr-0 flex min-w-0 flex-col gap-2 rounded-2xl border-l-[5px] bg-linear-to-b p-4 break-words shadow-(--golge-unk-kart)",
        durum
          ? [DURUM_SINIFLARI[durum].cizgi, KART_ZEMINI[durum]]
          : "border-l-notr-300 from-notr-50",
      )}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className="bg-mor-500 text-notr-0 grid size-9 shrink-0 place-items-center rounded-full text-[0.625rem] font-bold"
        >
          {dersKisaltmasi(g.dersAdi)}
        </span>
        <span className="text-metin-koyu min-w-0 flex-1 self-center text-lg leading-tight">
          {g.dersAdi ?? "Ders"}
        </span>
        {durum ? (
          <DurumRozeti durum={durum} className="shrink-0">
            {etiket}
          </DurumRozeti>
        ) : (
          <Rozet>{etiket}</Rozet>
        )}
      </div>
      <p className="text-metin-ikincil flex items-center gap-1 text-sm tabular-nums">
        <Ikon ad="takvim" className="size-3.5" />
        {noktaliTarih(g.itemDate)}
      </p>
      <dl className="text-metin-koyu flex flex-col gap-1 text-sm">
        {g.konuAdi && <Satir ad="Konu" deger={g.konuAdi} />}
        {g.kaynakAdi && <Satir ad="Kaynak" deger={g.kaynakAdi} />}
        {g.targetQuestionCount !== null && <Satir ad="Soru" deger={g.targetQuestionCount} />}
        {g.targetTestCount !== null && <Satir ad="Test" deger={g.targetTestCount} />}
      </dl>
      {(g.topicPdfUrl || g.testPdfUrl || g.youtubeUrl) && (
        <div className="flex flex-wrap gap-2">
          <HapBaglanti url={g.topicPdfUrl} etiket="Konu PDF" />
          <HapBaglanti url={g.testPdfUrl} etiket="Test PDF" />
          <HapBaglanti
            url={g.youtubeUrl}
            etiket={g.videoTitle ? `Video · ${g.videoTitle}` : "Video"}
          />
        </div>
      )}
      <div>
        <button
          type="button"
          aria-expanded={notAcik}
          onClick={() => setNotAcik((v) => !v)}
          className="min-h-dokunma bg-notr-0 ring-mor-kenar text-metin-koyu hover:bg-mor-50 inline-flex items-center gap-1.5 rounded-xl px-3 text-sm ring-1"
        >
          <Ikon ad="defter" className="size-4" />
          Notlar
        </button>
      </div>
      {notAcik && (
        <p className="border-notr-300 bg-notr-0 text-metin-koyu rounded-xl border border-dashed p-3 text-sm whitespace-pre-wrap">
          {g.coachNote ?? "Koç bu göreve not eklememiş."}
        </p>
      )}
      {durum === "eksik" && (
        <p className="border-bekliyor-nokta bg-bekliyor-zemin text-bekliyor-metin flex items-center gap-2 rounded-xl border border-dashed px-3 py-2 text-sm">
          <Ikon ad="uyari" className="size-4" />
          Bu ders için henüz rapor girmedin.
        </p>
      )}
      <p className="bg-saltokunur-zemin text-saltokunur-metin mt-auto flex items-center gap-2 rounded-xl px-3 py-2 text-sm">
        <Ikon ad="gorunum" className="size-4" />
        Salt-okunur mod
      </p>
    </li>
  );
}

function Satir({ ad, deger }: { ad: string; deger: string | number }) {
  return (
    <div>
      <dt className="inline font-bold">{ad}:</dt> <dd className="inline">{deger}</dd>
    </div>
  );
}

/**
 * Beyaz hap bağlantı. Şema kontrolü `ProgramBaglantisi` ile AYNI
 * (`baglantiSemasi`): `javascript:` gibi bir değer bağlantıya dönüşmüyor —
 * görünüm değişti diye güvenlik kuralı gevşemez.
 */
function HapBaglanti({ url, etiket }: { url: string | null; etiket: string }) {
  const sonuc = baglantiSemasi.safeParse(url);
  if (!sonuc.success) return null;
  return (
    <a
      href={sonuc.data}
      target="_blank"
      rel="noopener noreferrer"
      className="min-h-dokunma bg-notr-0 ring-mor-kenar text-metin-koyu hover:bg-mor-50 inline-flex max-w-full items-center gap-1.5 rounded-xl px-3 text-sm ring-1"
    >
      <Ikon ad="indir" className="size-4" />
      {etiket}
    </a>
  );
}

function KareDugme({
  etiket,
  onClick,
  children,
}: {
  etiket: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={etiket}
      className="bg-notr-0 ring-mor-kenar text-metin-koyu hover:bg-mor-50 grid size-11 place-items-center rounded-xl text-xl ring-1"
    >
      {children}
      <span className="sr-only">{etiket}</span>
    </button>
  );
}
